import React, { useState, useEffect, useCallback } from 'react'
import { Calendar, Layout, Divider, Badge, Popover, Input, Button } from 'antd'
import {
  SearchOutlined,
  LeftCircleOutlined,
  RightCircleOutlined,
  CaretDownOutlined,
  CheckOutlined
} from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import classnames from 'classnames'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { fetchAttendanceDaily } from '../../actions/attendance'
import CONFIG from '../../constants/config'
import sensors from '../../utils/sensors'
import parseQueryString from '../../utils/parseQueryString'
import TimeList from '../components/attendance/timeList'
import * as SentryUtils from '../../utils/sentryUtils'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

// 成员类型
interface Member {
  user_id: string
  nickname?: string
  headimgurl?: string
}

// 打卡时间类型
interface WorkTime {
  photoTime?: string
  status?: number
}

// 时间段类型
interface TimeBucket {
  onWork?: WorkTime
  offWork?: WorkTime
}

// 考勤数据类型
interface AttendanceDay {
  date: string
  status: number
  bucket?: TimeBucket[]
  isAskForLeave?: boolean
  leaveMinute?: number
  workHoursStatus?: number
  workSecond?: number
}

// 统计项组件 Props
interface StatisticsItemProps {
  value: number
  unit?: string
  desc: string
  valueStyle?: React.CSSProperties
}

// 成员选择器组件 Props
interface MemberPickerProps {
  members: Member[]
  checkedMember: string
  onOK: (checkedMember: string) => void
  onCancel: () => void
}

const StatisticsItem = ({
  value,
  unit,
  desc,
  valueStyle
}: StatisticsItemProps) => {
  return (
    <div className="item">
      <div>
        <span className="value" style={valueStyle}>
          {value}
        </span>
        <span>{unit || '次'}</span>
      </div>
      <div>
        <span>{desc}</span>
      </div>
    </div>
  )
}

const MemberPicker = ({
  members,
  checkedMember,
  onOK,
  onCancel
}: MemberPickerProps) => {
  const [checked, setChecked] = useState(checkedMember)
  const [keywords, setKeywords] = useState('')
  const onCancelClick = () => {
    setChecked(checkedMember)
    onCancel()
  }
  return (
    <div className="single-member-picker">
      <div className="search-wrapper">
        <Input
          placeholder="请输入搜索关键词"
          suffix={<SearchOutlined />}
          value={keywords}
          allowClear
          onChange={(e) => setKeywords(e.target.value)}
        />
      </div>
      <Divider style={{ margin: '0' }} />
      <div className="members-wrapper">
        {members
          .filter((member) => member.nickname?.includes(keywords))
          .map((member, index) => (
            <div
              className="member-item"
              key={index}
              style={
                checked === member.user_id ? { background: '#e6f7ff' } : {}
              }
              onClick={() => setChecked(member.user_id)}
            >
              <div>
                <img src={member.headimgurl} alt="" />
                <span>{member.nickname}</span>
              </div>
              {checked === member.user_id && (
                <CheckOutlined
                  style={{ color: '#207AFF', marginRight: '15px' }}
                />
              )}
            </div>
          ))}
      </div>
      <Divider style={{ margin: '0' }} />
      <div className="actions">
        <Button onClick={onCancelClick}>取消</Button>
        <Button type="primary" onClick={() => onOK(checked)}>
          确定
        </Button>
      </div>
    </div>
  )
}

const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / (60 * 60))}小时${Math.round(
    (seconds % (60 * 60)) / 60
  )}分钟`

function AttendanceCalendar() {
  const dispatch = useAppDispatch()

  // local state
  const [validRange, setValidRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ])
  const [checkedMemberId, setCheckedMemberId] = useState('')
  const [memberPickerVisible, setMemberPickerVisible] = useState(false)
  const [attendanceData, setAttendanceData] = useState<AttendanceDay[]>([])

  // Redux state
  const members = useAppSelector((state) => state.group.members)
  const groupId = useAppSelector((state) => state.group.groupId)
  const userId = useAppSelector((state) => state.main.user?.user_id)

  // componentDidMount
  useEffect(() => {
    const { user_id, date } = parseQueryString()
    const dateStr = date
      ? decodeURIComponent(date as string)
      : dayjs().format(CONFIG.dateFormat)
    const dates: [Dayjs, Dayjs] = [
      dayjs(dateStr).startOf('month'),
      dayjs(dateStr).endOf('month')
    ]
    setCheckedMemberId((user_id as string) || '')
    setValidRange(dates)

    const params = {
      groupID: groupId,
      userID: userId,
      parameters: {
        startDate: dates[0].format(CONFIG.dateFormat),
        endDate: dates[1].format(CONFIG.dateFormat),
        userIDList: [user_id],
        page: {
          isPagination: false
        }
      }
    }
    dispatch(fetchAttendanceDaily(params))
      .then((data) => {
        setAttendanceData(data.attendanceDatas || [])
      })
      .catch((err) => SentryUtils.capturePromiseException(err))
  }, [dispatch, groupId, userId])

  // 渲染日历头部
  const handleRenderCalendarHeader = useCallback(
    ({ value }: { value: Dayjs }) => {
      const { user_id, date } = parseQueryString()
      const prevDate = dayjs(date).add(-1, 'month').format(CONFIG.dateFormat)
      const nextDate = dayjs(date).add(1, 'month').format(CONFIG.dateFormat)
      return (
        <div className="pagination">
          <a
            onClick={() =>
              sensors.track('web_workgroup_attendance_person_calendar_click', {
                clickItem: 'previousMonth'
              })
            }
            href={`/attendance/calendar?user_id=${user_id}&date=${prevDate}`}
          >
            <LeftCircleOutlined />
          </a>
          <span style={{ margin: '0 50px' }}>
            {value.format(CONFIG.monthFormatZH)}
          </span>
          <a
            onClick={() =>
              sensors.track('web_workgroup_attendance_person_calendar_click', {
                clickItem: 'nextMonth'
              })
            }
            href={`/attendance/calendar?user_id=${user_id}&date=${nextDate}`}
          >
            <RightCircleOutlined />
          </a>
        </div>
      )
    },
    []
  )

  // 成员变更
  const handleMemberChange = useCallback((checkedMember: string) => {
    const { date } = parseQueryString()
    setCheckedMemberId(checkedMember)
    setMemberPickerVisible(false)
    window.location.href = `/attendance/calendar?user_id=${checkedMember}&date=${date}`
  }, [])

  // 获取状态文本
  const getStatus = useCallback((day: AttendanceDay | undefined) => {
    if (!day) {
      return null
    }

    const status = day.status

    return (
      {
        0: '出勤',
        1: '未出勤',
        2: '休息'
      }[status] || '出勤'
    )
  }, [])

  // 渲染异常状态
  const renderException = useCallback(
    ({
      isPreset,
      day,
      exceptionStatus
    }: {
      isPreset: boolean
      day: AttendanceDay | undefined
      exceptionStatus: string[]
    }) => {
      if (!day) {
        return null
      }

      if (day.isAskForLeave) {
        let text = '请假'

        if (isPreset && exceptionStatus.length > 0) {
          text += `、${exceptionStatus.join('、')}`
        }

        return <div className="exception-status">{text}</div>
      } else if (isPreset && exceptionStatus.length > 0) {
        return (
          <div className="exception-status">{exceptionStatus.join('、')}</div>
        )
      } else if (day.status === 1) {
        return <div className="exception-status">未出勤</div>
      }

      return null
    },
    []
  )

  // 渲染头部
  const renderHeader = useCallback(() => {
    const statistics = {
      presentDays: 0, // 出勤天数
      lateTimes: 0, // 迟到次数
      earlyTimes: 0, // 早退次数
      missTimes: 0, // 缺卡次数
      exceptionTimes: 0, // 工时异常次数
      leaveDays: 0, // 请假天数
      leaveMinutes: 0 // 请假时长
    }
    attendanceData.forEach((day) => {
      if (day.status < 1) {
        statistics.presentDays += 1
      }
      if (day.isAskForLeave) {
        statistics.leaveDays += 1
        statistics.leaveMinutes += day.leaveMinute || 0
      }

      if (day.status < 1) {
        // 只统计出勤的
        if (day.workHoursStatus === 1) {
          statistics.exceptionTimes += 1
        }
        ;(day.bucket || []).forEach((time) => {
          if (time.onWork?.status === 1) {
            statistics.lateTimes += 1
          }
          if (time.offWork?.status === 1) {
            statistics.lateTimes += 1
          }
          if (time.onWork?.status === 2) {
            statistics.earlyTimes += 1
          }
          if (time.offWork?.status === 2) {
            statistics.earlyTimes += 1
          }
          if (time.onWork?.status === 3) {
            statistics.missTimes += 1
          }
          if (time.offWork?.status === 3) {
            statistics.missTimes += 1
          }
        })
      }
    })
    return (
      <div className="Filter">
        <div
          className="filter-wrapper"
          style={{ justifyContent: 'space-between' }}
        >
          <Popover
            content={
              <MemberPicker
                members={members}
                onOK={handleMemberChange}
                onCancel={() => setMemberPickerVisible(false)}
                checkedMember={checkedMemberId}
              />
            }
            trigger="click"
            placement="bottomLeft"
            align={{ offset: [25, -20] }}
            open={memberPickerVisible}
            classNames={{ root: 'single-member-picker-overlay' }}
          >
            <div
              className="filter-item"
              onClick={() => {
                sensors.track(
                  'web_workgroup_attendance_person_calendar_click',
                  {
                    clickItem: 'switchPerson'
                  }
                )
                setMemberPickerVisible(true)
              }}
            >
              <span className="filter-name">
                筛选成员
                <CaretDownOutlined />
              </span>
              <p>
                {checkedMemberId &&
                  members.find(
                    (member) => member.user_id === checkedMemberId
                  ) &&
                  members.find((member) => member.user_id === checkedMemberId)
                    ?.nickname}
              </p>
            </div>
          </Popover>
          <div className="statistics">
            <StatisticsItem
              value={statistics.presentDays}
              valueStyle={{ color: 'rgba(38, 102, 222, 0.84)' }}
              unit="天"
              desc="出勤"
            />
            <StatisticsItem
              value={validRange[0].daysInMonth() - statistics.presentDays}
              valueStyle={{ color: 'rgba(38, 102, 222, 0.84)' }}
              unit="天"
              desc="未出勤"
            />
            <StatisticsItem
              value={statistics.leaveDays}
              valueStyle={{ color: 'rgba(38, 102, 222, 0.84)' }}
              unit="天"
              desc="请假"
            />
            <StatisticsItem
              value={Number((statistics.leaveMinutes / 60).toFixed(1))}
              valueStyle={{ color: 'rgba(38, 102, 222, 0.84)' }}
              unit="小时"
              desc="请假时长"
            />

            <Divider orientation="vertical" className="divider" />
            <StatisticsItem value={statistics.lateTimes} desc="迟到" />
            <StatisticsItem value={statistics.earlyTimes} desc="早退" />
            <StatisticsItem value={statistics.missTimes} desc="缺卡" />
            <StatisticsItem value={statistics.exceptionTimes} desc="工时异常" />
          </div>
        </div>
      </div>
    )
  }, [
    members,
    checkedMemberId,
    memberPickerVisible,
    attendanceData,
    validRange,
    handleMemberChange
  ])

  // 渲染日期单元格
  const renderDateCell = useCallback(
    (date: Dayjs) => {
      if (
        attendanceData.length > 0 &&
        date.isBetween(validRange[0], validRange[1], null, '[]')
      ) {
        const day = attendanceData.find(
          (day) => day.date === date.format(CONFIG.dateFormat)
        )
        const isPreset = day && day.status < 1

        let exceptionStatus: (string | number)[] = [] // 异常状态
        if (day) {
          ;(day.bucket || []).forEach((time) => {
            const onStatus = time.onWork?.status
            const offStatus = time.offWork?.status
            if (onStatus !== undefined && onStatus > 0 && onStatus < 4) {
              exceptionStatus.push(onStatus)
            }
            if (offStatus !== undefined && offStatus > 0 && offStatus < 4) {
              exceptionStatus.push(offStatus)
            }
          })
        }
        exceptionStatus = exceptionStatus.map((status) => {
          switch (status) {
            case 0:
              return '正常'
            case 1:
              return '迟到'
            case 2:
              return '早退'
            case 3:
              return '缺卡'
            default:
              return ''
          }
        })

        // 需要统计每种异常状态出现次数，通过hash进行保存和统计
        const exceptionStatusObj: Record<string, number> = {}
        for (const status of exceptionStatus) {
          const key = String(status)
          if (!exceptionStatusObj[key]) {
            exceptionStatusObj[key] = 1
          } else {
            exceptionStatusObj[key] += 1
          }
        }
        // 合并key-value
        exceptionStatus = Object.keys(exceptionStatusObj).map((status) => {
          return status + exceptionStatusObj[status]
        })

        return (
          <div
            className={classnames('custom-calendar-cell', {
              'non-attendance-cell': !isPreset
            })}
            onClick={() =>
              sensors.track('web_workgroup_attendance_person_calendar_click', {
                clickItem: 'calendarDay',
                calendarStatus: isPreset
                  ? exceptionStatus.length > 0
                    ? 'abnomal'
                    : 'nomal'
                  : 'none'
              })
            }
          >
            <div
              className={classnames('cell-content', {
                'exception-content': isPreset && exceptionStatus.length > 0
              })}
            >
              <div className="date">
                <h2 className="value">{date.date()}</h2>
                <p
                  style={{
                    color:
                      day &&
                      (day.status === 1
                        ? '#E02020'
                        : day.status === 2
                          ? 'rgba(0, 0, 0, 0.5)'
                          : '')
                  }}
                  className={classnames('attendance-status', {
                    'non-attendance-status': !isPreset
                  })}
                >
                  {getStatus(day)}
                </p>
                {isPreset && (
                  <p
                    className="work-hours"
                    style={
                      day.workHoursStatus === 1 ? { color: '#E02020' } : {}
                    }
                  >
                    {formatDuration(day.workSecond || 0)}
                  </p>
                )}
              </div>
              <div className="clock-in-records">
                {isPreset && (
                  <ul>
                    {(day.bucket || [])
                      .filter((item, index) => {
                        if ((day.bucket || []).length <= 5) {
                          return true
                        } else {
                          return index <= 3
                        }
                      })
                      .map((time, index) => {
                        const startTime = time.onWork?.photoTime
                        const endTime = time.offWork?.photoTime
                        const onStatus = time.onWork?.status ?? 0
                        const offStatus = time.offWork?.status ?? 0
                        const statusArr = [1, 2, 3]

                        return (
                          <li key={index}>
                            <Badge
                              status={
                                statusArr.includes(onStatus) ||
                                statusArr.includes(offStatus)
                                  ? 'error'
                                  : 'success'
                              }
                              text={
                                <>
                                  <span
                                    style={{
                                      width: '40px',
                                      display: 'inline-block',
                                      color: statusArr.includes(onStatus)
                                        ? '#E02020'
                                        : '#292B2E'
                                    }}
                                  >
                                    {startTime}
                                  </span>
                                  {offStatus !== 5 && (
                                    <>
                                      <span>-</span>
                                      <span
                                        style={{
                                          width: '40px',
                                          display: 'inline-block',
                                          color: statusArr.includes(offStatus)
                                            ? '#E02020'
                                            : '#292B2E'
                                        }}
                                      >
                                        {endTime}
                                      </span>
                                    </>
                                  )}
                                </>
                              }
                            />
                          </li>
                        )
                      })}
                    <li>
                      {isPreset && (day.bucket || []).length >= 5 && (
                        <TimeList day={day} isPreset={isPreset} />
                      )}
                    </li>
                  </ul>
                )}
              </div>
            </div>
            {renderException({
              isPreset: isPreset ?? false,
              day,
              exceptionStatus: exceptionStatus as string[]
            })}
          </div>
        )
      }
      return (
        <div className="out-of-date-range-cell">
          <div className="cell-content">
            <div className="date">
              <h2 className="value">{date.date()}</h2>
            </div>
          </div>
        </div>
      )
    },
    [attendanceData, validRange, getStatus, renderException]
  )

  return (
    <Layout className="Calendar">
      <PageHeader
        ghost={false}
        style={{ borderBottom: '1px solid rgb(235, 237, 240)' }}
        title="考勤日历"
        extra={
          <Button
            type="primary"
            onClick={() => (window.location.href = `/attendance/period`)}
          >
            返回列表
          </Button>
        }
      />
      <Content className="content">
        {renderHeader()}
        <div className="calendar-container">
          <Calendar
            validRange={validRange}
            className="calendar"
            headerRender={handleRenderCalendarHeader}
            fullCellRender={renderDateCell}
            value={validRange[0]}
            onSelect={(date: Dayjs) =>
              window.open(
                `/photo?start_time=${encodeURIComponent(
                  date.startOf('day').format(CONFIG.minuteFormat)
                )}&end_time=${encodeURIComponent(
                  date.endOf('day').format(CONFIG.minuteFormat)
                )}&member_id=${encodeURIComponent(checkedMemberId)}`
              )
            }
          />
        </div>
      </Content>
    </Layout>
  )
}

export { AttendanceCalendar }
export default AttendanceCalendar
